class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
		this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.debounceTimer = null;

		this.setupEventListeners();
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.input.addEventListener("input", this.onInput.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
		this.addEventListener("keydown", this.onKeydown.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	onInput() {
		clearTimeout(this.debounceTimer);
		this.debounceTimer = setTimeout(() => {
			this.onChange();
		}, 300);
	}

	onChange() {
		super.onChange();
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);

		this.open();
	}

	onFormSubmit(event) {
		if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();

		if (!currentSearchTerm.length) return;

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) this.close();
		});
	}

	onKeyup(event) {
		if (!this.getQuery().length) {
			this.close(true);
			return;
		}

		if (event.key === "Backspace" || event.key === "Delete") {
			clearTimeout(this.debounceTimer);
			this.onChange();
			return;
		}

		switch (event.code) {
			case "ArrowUp":
				this.switchOption("up");
				break;
			case "ArrowDown":
				this.switchOption("down");
				break;
			case "Enter":
				this.selectOption();
				break;
		}
	}

	onKeydown(event) {
		// Prevent the cursor from moving in the input when using the up and down arrow keys
		if (event.code === "ArrowUp" || event.code === "ArrowDown") {
			event.preventDefault();
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			const matches = currentButtonText.match(new RegExp(previousTerm, "g"));
			if (matches && matches.length > 1) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
	}

	switchOption(direction) {
		if (!this.getAttribute("open")) return;

		const moveUp = direction === "up";
		const selectedElement = this.querySelector('[aria-selected="true"]');

		// Filter out hidden elements (duplicated page and article resources) thanks
		// to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
		const allVisibleElements = Array.from(this.querySelectorAll("li, button.predictive-search__item")).filter((element) => element.offsetParent !== null);

		let activeElementIndex = 0;
		let selectedElementIndex = allVisibleElements.indexOf(selectedElement);

		if (moveUp && !selectedElement) return;

		this.statusElement.textContent = "";

		if (!moveUp && selectedElement) {
			activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
		} else if (moveUp) {
			activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
		}

		if (activeElementIndex === selectedElementIndex) return;

		const activeElement = allVisibleElements[activeElementIndex];

		activeElement.setAttribute("aria-selected", true);
		if (selectedElement) selectedElement.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", activeElement.id);
	}

	selectOption() {
		const selectedElement = this.querySelector('[aria-selected="true"]');

		if (selectedElement) {
			const link = selectedElement.querySelector("a, button");
			if (link) {
				link.click();
			}
		}
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.setLiveRegionResults();
			return;
		}

		// Use normal search API which includes SKU search by default
		// The normal search API searches in: title, description, tags, AND SKU
		const url = `/search?q=${encodeURIComponent(searchTerm)}&type=product&options[prefix]=last&section_id=bs-predictive-search`;

		fetch(url, { signal: this.abortController.signal })
			.then((response) => {
				if (!response.ok) {
					this.close();
					throw new Error(`HTTP ${response.status}`);
				}

				return response.text();
			})
			.then((text) => {
				const html = new DOMParser().parseFromString(text, "text/html");
				const section = html.querySelector("#shopify-section-bs-predictive-search");

				if (!section) {
					this.close();
					return;
				}

				const resultsMarkup = section.innerHTML;

				this.allPredictiveSearchInstances.forEach((instance) => {
					if (instance.cachedResults !== undefined) {
						instance.cachedResults[queryKey] = resultsMarkup;
					}
				});

				this.renderSearchResults(resultsMarkup);
				this.setLiveRegionResults();
			})
			.catch((error) => {
				if (error?.name === "AbortError" || error?.code === 20) {
					return;
				}

				this.close();
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		const collectionLinks = this.predictiveSearchResults.querySelectorAll("a[data-collection-key]");

		collectionLinks.forEach((link) => {
			const key = link.getAttribute("data-collection-key");
			if (key) {
				link.href = this.getCollectionUrlByTag(key);
			}
		});

		requestAnimationFrame(() => {
			this.addSearchMenuItems();
			this.renderSearchTags();
			this.initSearchMenuFilter();
			this.setLiveRegionResults();
			this.open();
		});
	}

	addSearchMenuItems() {
		const sourceMenu = document.querySelector("#header-search-target .search-menu") || document.querySelector("#menu-drawer-search-block-target .search-menu");

		const targetMenu = this.predictiveSearchResults.querySelector(".search-menu");

		if (!sourceMenu || !targetMenu) return;

		targetMenu.innerHTML = "";
		Array.from(sourceMenu.childNodes).forEach((child) => {
			targetMenu.appendChild(child.cloneNode(true));
		});
	}

	buildTagGroupsFromProducts() {
		const productItems = this.predictiveSearchResults.querySelectorAll(".predictive-search__list-item");

		const tagMap = {};

		productItems.forEach((item) => {
			const collectionsRaw = item.dataset.collections || "";
			const collections = collectionsRaw
				.split(/[\s,]+/)
				.map((c) => c.toLowerCase().trim())
				.filter(Boolean);

			const tagsRaw = item.dataset.tags || "";
			const tags = tagsRaw
				.split(/[\s,]+/)
				.map((t) => t.toLowerCase().trim())
				.filter(Boolean);

			collections.forEach((group) => {
				if (!tagMap[group]) tagMap[group] = new Set();

				tags.forEach((tag) => {
					if (tag !== group) tagMap[group].add(tag);
				});
			});
		});

		return tagMap;
	}

	renderSearchTags() {
		const wrapper = this.predictiveSearchResults.querySelector(".search-tags-wrapper");
		if (!wrapper) return;

		wrapper.innerHTML = "";

		const tagMap = this.buildTagGroupsFromProducts();

		Object.entries(tagMap).forEach(([group, tagsSet]) => {
			const tags = Array.from(tagsSet);
			if (!tags.length) return;

			const groupDiv = document.createElement("div");
			groupDiv.classList.add("search-tags");
			groupDiv.dataset.group = group;

			tags.forEach((tag) => {
				const tagEl = document.createElement("div");
				tagEl.className = "tag text-s text-primary";
				tagEl.dataset.tag = tag;
				tagEl.textContent = tag;
				groupDiv.appendChild(tagEl);
			});

			wrapper.appendChild(groupDiv);
		});
	}

	initSearchMenuFilter() {
		const container = this.predictiveSearchResults;
		const buttons = container.querySelectorAll(".search-menu__item");
		const productItems = container.querySelectorAll(".predictive-search__list-item");
		const tagGroups = container.querySelectorAll(".search-tags");

		if (!buttons.length || !productItems.length) return;

		buttons.forEach((button, i) => {
			if (i === 0) button.classList.add("search-menu__item--active");
			else button.classList.remove("search-menu__item--active");
		});

		tagGroups.forEach((group, index) => {
			group.classList.toggle("search-tags--active", index === 0);

			const tags = group.querySelectorAll(".tag");
			tags.forEach((tag) => {
				tag.addEventListener("click", () => {
					const isActive = tag.classList.contains("tag--active");

					if (isActive) {
						tag.classList.remove("tag--active");
					} else {
						tags.forEach((t) => t.classList.remove("tag--active"));
						tag.classList.add("tag--active");
					}

					this.filterProducts();
				});
			});
		});

		buttons.forEach((button) => {
			button.replaceWith(button.cloneNode(true));
		});

		const freshButtons = container.querySelectorAll(".search-menu__item");

		const updateVisibleProductCount = () => {
			const visibleCount = Array.from(productItems).filter((item) => item.style.display !== "none").length;
			const countElement = container.querySelector(".predictive-search__number");
			if (countElement) {
				countElement.textContent = visibleCount;
			}
		};

		freshButtons.forEach((button) => {
			button.addEventListener("click", () => {
				freshButtons.forEach((b) => b.classList.remove("search-menu__item--active"));
				button.classList.add("search-menu__item--active");

				const selected = button.dataset.item.toLowerCase();

				tagGroups.forEach((group) => {
					const groupName = group.dataset.group?.toLowerCase();
					const isActive = groupName === selected;
					group.classList.toggle("search-tags--active", isActive);

					if (isActive) {
						group.querySelectorAll(".tag.tag--active").forEach((activeTag) => activeTag.classList.remove("tag--active"));
					}
				});

				this.filterProducts();
			});
		});

		this.filterProducts = () => {
			const activeButton = container.querySelector(".search-menu__item--active");
			const activeCollection = activeButton ? activeButton.dataset.item.toLowerCase() : null;
			const activeTagGroup = container.querySelector(".search-tags--active");
			const activeTags = activeTagGroup ? Array.from(activeTagGroup.querySelectorAll(".tag--active")).map((tag) => tag.dataset.tag.toLowerCase().trim()) : [];

			productItems.forEach((item) => {
				const collections = (item.dataset.collections || "").split(/[\s,]+/).map((t) => t.toLowerCase().trim());
				const tags = (item.dataset.tags || "").split(/[\s,]+/).map((t) => t.toLowerCase().trim());

				const matchesCollection = !activeCollection || collections.includes(activeCollection);

				const matchesTags = !activeTags.length || activeTags.some((activeTag) => tags.includes(activeTag));

				if (matchesCollection && matchesTags) {
					item.style.display = "";
				} else {
					item.style.display = "none";
				}
			});

			const viewAllBtn = container.querySelector(".predictive-search__item--term");
			if (viewAllBtn) {
				viewAllBtn.href = this.getActiveFiltersUrl();
			}

			updateVisibleProductCount();
		};

		this.filterProducts();
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		this.setLiveRegionText(this.querySelector("[data-predictive-search-live-region-count-value]").textContent);
	}

	getActiveFiltersUrl() {
		const container = this.predictiveSearchResults;

		const activeButton = container.querySelector(".search-menu__item--active");
		const collectionTag = activeButton?.dataset.item.toLowerCase().trim();

		const activeTagGroup = container.querySelector(".search-tags--active");
		const activeTags = activeTagGroup ? Array.from(activeTagGroup.querySelectorAll(".tag--active")).map((tag) => tag.dataset.tag.toLowerCase().trim()) : [];

		const allTags = [collectionTag, ...activeTags].filter(Boolean);

		if (!allTags.length) return "/collections/all";

		const tagString = allTags.map(encodeURIComponent).join("+");
		return `/collections/all/${tagString}`;
	}

	getResultsMaxHeight() {
		this.resultsMaxHeight = window.innerHeight - document.querySelector(".section-header")?.getBoundingClientRect().bottom;
		return this.resultsMaxHeight;
	}

	open() {
		this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";

			this.removeAttribute("results");
		}

		const selected = this.querySelector('[aria-selected="true"]');
		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.isOpen = false;
		this.input.setAttribute("aria-expanded", false);
		this.resultsMaxHeight = false;

		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.innerHTML = "";
			this.predictiveSearchResults.removeAttribute("style");
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);

document.addEventListener("DOMContentLoaded", () => {
	const detailsList = document.querySelectorAll(".header__search details");

	detailsList.forEach((details) => {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.attributeName === "open") {
					const isOpen = details.hasAttribute("open");

					if (!isOpen) {
						setTimeout(() => {
							const predictive = details.querySelector("predictive-search");
							if (predictive) {
								predictive.close(true);
							}
						}, 50);
					}
				}
			});
		});
		observer.observe(details, { attributes: true });
	});
});
